import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { CustomerProductActionsCell } from 'src/components/customer-product/grid/CustomerProductActionsCell'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { formatCustomerProductTimestamp } from 'src/utils/customerProductList'
import { customerProductLabels, customerProductTableHeaderTitles } from './constants'
import { CustomerProductsTableFields } from './enums'

/**
 * Export/copy values for the two timestamp columns. `renderCell` governs what the admin
 * sees; these govern what leaves the grid, and they deliberately mirror the rendered text
 * so a CSV never disagrees with the screen. `startTime` falls back to empty (the on-screen
 * em-dash is a UI affordance, not data); `endTime` falls back to `Ongoing`, which IS the
 * meaning of a null end date.
 */
const formatStartTimeValue = (value: string | null): string => {
  return formatCustomerProductTimestamp(value) ?? ''
}

const formatEndTimeValue = (value: string | null): string => {
  return formatCustomerProductTimestamp(value) ?? 'Ongoing'
}

/**
 * Wiring only — timestamp handling comes from utils/customerProductList, actions from
 * CustomerProductActionsCell.
 *
 * A factory rather than a module constant: the actions column closes over the page's edit and
 * deactivate handlers. Callers must memoize the result, since a fresh array on every render
 * would reset the grid's column state.
 */
export const buildCustomerProductColumns = (
  onEditProduct: (product: CustomerProduct) => void,
  onDeactivateProduct: (product: CustomerProduct) => void,
  // `partner_products_manage` (ENG-1207 part 2). Both row actions need it, so without it the
  // actions column is dropped rather than left as an empty menu.
  canManage: boolean,
): Array<GridColDef<CustomerProduct>> => {
  const columns: Array<GridColDef<CustomerProduct>> = [
    {
      field: CustomerProductsTableFields.id,
      headerName: customerProductTableHeaderTitles[CustomerProductsTableFields.id],
      width: 300,
      sortable: true,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<CustomerProduct>) => {
        return (
          <Typography variant="body2" css={tw`truncate text-shades-500`}>
            {params.row.id}
          </Typography>
        )
      },
    },
    {
      field: CustomerProductsTableFields.product,
      headerName: customerProductTableHeaderTitles[CustomerProductsTableFields.product],
      flex: 2,
      minWidth: 180,
      sortable: true,
      // The product identifies the row, so it is never offered for hiding.
      hideable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<CustomerProduct>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {customerProductLabels[params.row.product]}
          </Typography>
        )
      },
    },
    {
      field: CustomerProductsTableFields.startTime,
      headerName: customerProductTableHeaderTitles[CustomerProductsTableFields.startTime],
      width: 180,
      sortable: true,
      display: 'flex',
      // `renderCell` wins for display, so this only shapes the exported/copied value — but
      // without it a CSV would carry the raw backend timestamp, silently disagreeing with
      // the UTC text on screen. Every other grid in this app formats dates this way.
      valueFormatter: formatStartTimeValue,
      renderCell: (params: GridRenderCellParams<CustomerProduct>) => {
        const formatted = formatCustomerProductTimestamp(params.row.startTime)

        if (!formatted) {
          // A dash for a missing optional value is a cosmetic default, not fabricated content.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return <Typography variant="body2">{formatted}</Typography>
      },
    },
    {
      field: CustomerProductsTableFields.endTime,
      headerName: customerProductTableHeaderTitles[CustomerProductsTableFields.endTime],
      width: 180,
      sortable: true,
      display: 'flex',
      valueFormatter: formatEndTimeValue,
      renderCell: (params: GridRenderCellParams<CustomerProduct>) => {
        const formatted = formatCustomerProductTimestamp(params.row.endTime)

        if (!formatted) {
          // A null endTime is the backend's own representation of "no end date", so this is a
          // label for a real state rather than a placeholder for missing data.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              Ongoing
            </Typography>
          )
        }

        return <Typography variant="body2">{formatted}</Typography>
      },
    },
    {
      field: CustomerProductsTableFields.actions,
      headerName: customerProductTableHeaderTitles[CustomerProductsTableFields.actions],
      width: 68,
      sortable: false,
      // The row's only edit/deactivate affordance, so it is never offered for hiding —
      // matching the Markets actions column.
      hideable: false,
      disableExport: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<CustomerProduct>) => {
        return (
          <CustomerProductActionsCell
            product={params.row}
            onEdit={onEditProduct}
            onDeactivate={onDeactivateProduct}
          />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== CustomerProductsTableFields.actions
  })
}
